// ─── seo/RouteSeoDefaults.jsx ────────────────────────────────────────────────
// Baseline <head> for every route, rendered once above the router outlet.
//
// Its real job is keeping account surfaces out of the index: dashboards,
// inboxes, checkout, and staff tools have no search value and leak UI into
// results if crawled. Doing it here — rather than adding a noindex flag to
// twenty-odd page files — means a new private route is covered the moment its
// path matches a prefix, which is the safer default.
//
// Pages that render their own <Seo> override these tags: Helmet resolves
// duplicates in favour of the deeper (later-mounted) instance.
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME, DEFAULT_DESCRIPTION, PRIVATE_PATH_PREFIXES } from './siteMeta';

export function isPrivatePath(pathname) {
  return PRIVATE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix)
  );
}

// Tab titles for routes that don't render their own <Seo> (account pages
// mostly), so open tabs say what they are instead of all reading
// "UniFreelancer". First matching prefix wins, so specific paths come first.
const ROUTE_TITLES = [
  ['/inbox', 'Inbox'],
  ['/work/order/', 'Order'],
  ['/work/contract/', 'Contract'],
  ['/seller/dashboard', 'Selling dashboard'],
  ['/hiring/dashboard', 'Hiring dashboard'],
  ['/instructor/dashboard', 'Teaching dashboard'],
  ['/instructor/courses/', 'Manage course'],
  ['/instructor/grade/', 'Grading'],
  ['/instructor/module-submission/', 'Review submission'],
  ['/mentor/dashboard', 'Mentor dashboard'],
  ['/professor', 'Studio'],
  ['/classes/', 'Class'],
  ['/admin', 'Admin'],
  ['/calendar', 'Calendar'],
  ['/notifications', 'Notifications'],
  ['/my-agreements', 'Agreements'],
  ['/saved', 'Saved'],
  ['/profile/close', 'Close account'],
  ['/profile', 'Settings'],
  ['/settings', 'Settings'],
  ['/academy/my-library', 'My library'],
  ['/academy/create', 'Create'],
  ['/academy/courses/', 'Course'],
  ['/academy/seminars/', 'Seminar'],
  ['/academy/tutorials/', 'Tutorial'],
  ['/academy/podcasts/', 'Podcast'],
  ['/academy/project-studio/', 'Project Studio'],
  ['/academy/submissions/', 'Submission'],
  ['/find-work/create-listing', 'Post a service'],
  ['/find-work/edit-listing/', 'Edit service'],
  ['/job-postings/create', 'Post a job'],
  ['/job-postings/edit/', 'Edit job'],
  ['/teams/new', 'Create a team'],
  ['/teams/', 'Team'],
  ['/teams', 'Teams'],
  ['/login', 'Log in'],
  ['/forgot-password', 'Reset your password'],
  ['/reset-password', 'Reset your password'],
  ['/verify-email', 'Verify your email'],
  ['/get-started', 'Get started'],
  ['/seller-onboarding', 'Start selling'],
];

// The page's plain name ("Inbox", "Order"), or null. Also used by Login to
// say where you're headed ("Log in to continue to your inbox").
export function pageNameFor(pathname) {
  const hit = ROUTE_TITLES.find(([prefix]) => pathname.startsWith(prefix));
  return hit ? hit[1] : null;
}

function titleFor(pathname) {
  const name = pageNameFor(pathname);
  return name ? `${name} | ${SITE_NAME}` : SITE_NAME;
}

export default function RouteSeoDefaults() {
  const { pathname } = useLocation();
  const priv = isPrivatePath(pathname);

  return (
    <Helmet>
      <title>{titleFor(pathname)}</title>
      <meta name="description" content={DEFAULT_DESCRIPTION} />
      {priv && <meta name="robots" content="noindex,nofollow" />}
    </Helmet>
  );
}
